import {useCallback, useEffect, useRef, useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {AppointmentBookingEnum} from 'main/analytics/gaTagsEnum';
import {FavoritesEnum, FeaturesEnum, HomeEnum, QueryParamsEnum} from 'presentation/constants';
import {useFeatureFlag} from 'presentation/hooks/useFeatureFlag';
import {getHealthStepPosition} from 'presentation/modules/booking/health/utils/getHealthStepPosition';
import {useBookingSummary} from 'presentation/modules/booking/shared/hooks/useBookingSummary';
import {useHideSearchBarStore} from 'presentation/modules/booking/shared/store/useHideSearchBarStore';
import {useHideStepBarStore} from 'presentation/modules/booking/shared/store/useHideStepBarStore';
import {useUnitsListStore} from 'presentation/modules/booking/shared/store/useUnitsListStore';
import {useAccountInformation, useMediaQueries} from 'presentation/pages';
import {useQueryParamsSync} from 'presentation/pages/private/booking/hooks/useQueryParamsSync';
import {translations} from 'presentation/translations-new';
import {HEADER_TITLES} from '../../constants/headerTitles';
import {SEARCH_BAR_PLACEHOLDER} from '../../constants/searchBarPlaceholder';
import {HEALTH_LABELS, HEALTH_LABELS_GEO} from '../../constants/stepLabels';
import {BookingOriginEnum} from 'presentation/modules/booking/shared/constants/bookingOriginEnum';
import {useBackButtonBooking} from 'presentation/modules/booking/shared/hooks/useBackButtonBooking';
import {BookingTypeEnum} from 'presentation/modules/booking/shared/constants/bookingTypeEnum';

const {bookHealth} = translations['pt-br'].privateFlow.services;
const {searchSpecialty, scheduleScript} = translations['pt-br'].privateFlow.bookingFlow;

export function useHealthBooking() {
	const stepsVisited = useRef(new Set<number>());
	const [isFirstVisit, setIsFirstVisit] = useState(true);
	const {isMobile, isLaptop, isDesktop} = useMediaQueries();
	const hideSearchBar = useHideSearchBarStore(state => state.hideSearchBar);
	const setHideSearchBar = useHideSearchBarStore(state => state.setHideSearchBar);
	const hideStepBar = useHideStepBarStore(state => state.hideStepBar);
	const setHideStepBar = useHideStepBarStore(state => state.setHideStepBar);
	const resetUnitResponseState = useUnitsListStore(state => state.resetUnitsList);
	const {isOurDoctorPlan} = useAccountInformation();
	const [openScriptV2BackModal, setOpenScriptV2BackModal] = useState(false);
	const {isEnabled: scheduleScriptFlagIsEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_SCHEDULE_SCRIPT,
	);
	const {isEnabled: ourDoctorFlagIsEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_NOSSO_MEDICO,
	);
	const {isEnabled: isNewResumePageEnabled} = useFeatureFlag(
		FeaturesEnum.PORTAL_FEATURE_NEW_RESUME_BOOKING_PAGE,
	);

	const navigate = useNavigate();
	const {
		activeBookingFlowStep,
		bookingData,
		setActiveBookingFlowStep,
		setBookingData,
		setSearchParams,
		clearSearch,
	} = useQueryParamsSync({isFirstVisit});

	const {
		handleChangeModals,
		openDrawerState,
		setOpenDrawerState,
		abandonFlowState,
		setAbandonFlowState,
	} = useBookingSummary();

	useBackButtonBooking({flowType: BookingTypeEnum.HEALTH});

	const isUsingGeolocation = !bookingData.isManualLocation;

	const {
		LOCATION_STEP,
		SPECIALITY_OR_EXAM_STEP,
		UNIT_STEP,
		PROFESSIONAL_STEP,
		REVIEW_STEP,
		DISTRICT_STEP,
	} = getHealthStepPosition({
		isManualLocation: bookingData.isManualLocation,
	});

	const getHeaderTitle = () => {
		if (bookingData.isScheduleScript) {
			return scheduleScript.title;
		}

		if (!isMobile) return bookHealth;

		return isUsingGeolocation
			? HEADER_TITLES.withGeo[activeBookingFlowStep]
			: HEADER_TITLES.withoutGeo[activeBookingFlowStep];
	};

	const shouldHideBackBtn = bookingData.isRebooking && activeBookingFlowStep === DISTRICT_STEP;
	const stepLabels = isUsingGeolocation ? HEALTH_LABELS_GEO : HEALTH_LABELS;
	const searchBarPlaceholder = isUsingGeolocation
		? SEARCH_BAR_PLACEHOLDER.withGeo
		: SEARCH_BAR_PLACEHOLDER.withoutGeo;

	const isLastStep = activeBookingFlowStep === stepLabels.length - 1;
	const isScheduleScriptActive = scheduleScriptFlagIsEnabled && bookingData.isScheduleScript;

	const shouldHideSearchBarAndBookingSummary = activeBookingFlowStep === LOCATION_STEP;
	const shouldHideSearchBar =
		hideSearchBar ||
		isLastStep ||
		shouldHideSearchBarAndBookingSummary ||
		isScheduleScriptActive;

	const showSwipeableDrawerBookingSummary =
		!isLastStep && isLaptop && !shouldHideSearchBarAndBookingSummary;

	const shouldUseNewResumeLayout = isNewResumePageEnabled && isLastStep;

	const showHealthSummarySectionGroupInGrid =
		isDesktop && !shouldHideSearchBarAndBookingSummary && !shouldUseNewResumeLayout;

	const shouldShowSpecialtyTagging =
		SEARCH_BAR_PLACEHOLDER[activeBookingFlowStep] === searchSpecialty;

	const shouldShowOurDoctorAlert =
		ourDoctorFlagIsEnabled &&
		isOurDoctorPlan &&
		activeBookingFlowStep === SPECIALITY_OR_EXAM_STEP &&
		!isScheduleScriptActive;

	const mainHeaderBackButtonTagId = isScheduleScriptActive
		? AppointmentBookingEnum.CLICK_VOLTAR_SCRIPT
		: AppointmentBookingEnum.CLICK_VOLTAR_AGENDAMENTO;
	const mainHeaderCloseButtonTagId = isScheduleScriptActive
		? AppointmentBookingEnum.CLICK_FECHAR_SCRIPT
		: AppointmentBookingEnum.CLICK_DESCARTAR_AGENDAMENTO;

	const searchBarTagId = shouldShowSpecialtyTagging
		? AppointmentBookingEnum.CLICK_BUSCAR_ESPECIALIDADE
		: undefined;

	const shouldOpenBackScriptModal = Boolean(
		bookingData.isScheduleScript && isScheduleScriptActive,
	);

	const handleClose = useCallback(() => {
		setAbandonFlowState(true);
	}, []);

	const handleBackHealth = useCallback(() => {
		clearSearch();

		if (activeBookingFlowStep === LOCATION_STEP) {
			handleClose();
			return;
		}

		if (activeBookingFlowStep === SPECIALITY_OR_EXAM_STEP) {
			setBookingData(prev => ({
				...prev,
				isManualLocation: true,
			}));
		}

		if (activeBookingFlowStep === DISTRICT_STEP) {
			setBookingData(prev => ({
				...prev,
				isRebooking: false,
				bookingNumber: '',
				specialtyCode: '',
				subSpecialtyCode: '',
				specialtyName: '',
				isScheduleScript: false,
				districtName: '',
				allDistrictsSelected: false,
				citiesNames: '',
				bookingOrigin: BookingOriginEnum.BOOKING_HEALTH,
			}));
		}

		if (activeBookingFlowStep === UNIT_STEP) {
			setSearchParams(
				prev => {
					prev.delete(QueryParamsEnum.LOCALITY_DISTANCE);
					return prev;
				},
				{
					replace: true,
				},
			);

			if (bookingData?.isFavoriteDoctorsFlow) {
				const favoritesUrl = bookingData.specialtyName
					? `${FavoritesEnum.FAVORITE_DOCTORS}?specialty=${encodeURIComponent(bookingData.specialtyName)}`
					: FavoritesEnum.FAVORITE_DOCTORS;
				navigate(favoritesUrl, {replace: true});
				return;
			}

			if (bookingData.shouldBackToSpecialtyStep) {
				setActiveBookingFlowStep(SPECIALITY_OR_EXAM_STEP);
				return;
			}
		}

		if (activeBookingFlowStep === PROFESSIONAL_STEP) {
			setBookingData(prev => ({
				...prev,
				unitCode: '',
				unitAddress: '',
				unitName: '',
				dateAt: '',
			}));
		}

		if (activeBookingFlowStep === REVIEW_STEP) {
			setBookingData(prev => ({
				...prev,
				hourAt: '',
				dateAt: '',
				professionalCode: '',
				professionalName: '',
			}));

			setSearchParams(
				prev => {
					prev.delete(QueryParamsEnum.SHIFT);
					prev.delete(QueryParamsEnum.AUTOCOMPLETE_QUERY);

					return prev;
				},
				{
					replace: true,
				},
			);
		}

		setActiveBookingFlowStep(value => value - 1);
	}, [activeBookingFlowStep, bookingData.shouldBackToSpecialtyStep]);

	const handleBack = () => {
		if (shouldOpenBackScriptModal) {
			return handleBackScheduleScriptV2Opened();
		}

		return handleBackHealth();
	};

	const handleConfirmExitFlow = useCallback(() => {
		setAbandonFlowState(false);
		navigate(HomeEnum.HOME_PAGE);
	}, []);

	const shouldResetUnitResponseState = [
		LOCATION_STEP,
		SPECIALITY_OR_EXAM_STEP,
		DISTRICT_STEP,
		UNIT_STEP,
	].includes(activeBookingFlowStep);

	const handleNext = () => {
		if (activeBookingFlowStep === stepLabels.length) {
			return;
		}
		setSearchParams(prev => {
			prev.delete(QueryParamsEnum.SEARCH_QUERY);
			return prev;
		});
		if (shouldResetUnitResponseState) {
			resetUnitResponseState();
		}
		if (isUsingGeolocation && activeBookingFlowStep < UNIT_STEP) {
			setBookingData(prev => ({
				...prev,
				hasGeoDistrictsFilter: false,
			}));
		}

		setActiveBookingFlowStep(value => value + 1);
	};

	const customFooterMarginBottom = () => {
		if (isUsingGeolocation) return undefined;
		if (activeBookingFlowStep === DISTRICT_STEP && isMobile) {
			return '142px';
		}

		return undefined;
	};

	const handleBackScheduleScriptV2Opened = useCallback(() => {
		setOpenScriptV2BackModal(true);
	}, []);

	const handleBackScheduleScriptV2Confirmed = useCallback(() => {
		setOpenScriptV2BackModal(false);
		handleBackHealth();
	}, [handleBackHealth]);

	const handleBackScheduleScriptV2Cancelled = useCallback(() => {
		setOpenScriptV2BackModal(false);
	}, []);

	useEffect(() => {
		const isFirstVisitValue = !stepsVisited.current.has(activeBookingFlowStep);
		setIsFirstVisit(isFirstVisitValue);
		stepsVisited.current.add(activeBookingFlowStep);
	}, [activeBookingFlowStep]);

	useEffect(() => {
		setHideSearchBar(false);
		setHideStepBar(false);
		clearSearch();
	}, [activeBookingFlowStep]);

	return {
		getHeaderTitle,
		handleBack,
		handleClose,
		shouldHideBackBtn,
		isMobile,
		isLaptop,
		activeBookingFlowStep,
		stepLabels,
		hideStepBar,
		isLastStep,
		shouldHideSearchBarAndBookingSummary,
		customFooterMarginBottom,
		setActiveBookingFlowStep,
		abandonFlowState,
		setAbandonFlowState,
		handleConfirmExitFlow,
		showSwipeableDrawerBookingSummary,
		openDrawerState,
		setOpenDrawerState,
		handleChangeModals,
		shouldHideSearchBar,
		searchBarPlaceholder,
		handleNext,
		isScheduleScriptActive,
		mainHeaderBackButtonTagId,
		mainHeaderCloseButtonTagId,
		shouldShowOurDoctorAlert,
		searchBarTagId,
		handleBackScheduleScriptV2Confirmed,
		handleBackScheduleScriptV2Cancelled,
		openScriptV2BackModal,
		shouldUseNewResumeLayout,
		showHealthSummarySectionGroupInGrid,
	};
}
